<template>
  <div class="tardy-reports-datatable-container">
    <Loader
      :is-processing="state.isLoading"
      :loader-text="$t('common.a11y_loading_tardy_reports')"
    />
    <ActionPanel
      @tab-clicked="setActiveTab"
      class="mb-3"
      show-sort
      show-export
      :export-loading="state.isLoading"
      :tabs="tabs"
      @on-sort="toggleFilter"
      @on-export="exportCSV"
    >
      <template #bottom-content>
        <!-- Filters and actions are split so Submit/Clear stay in the tab order when filters are collapsed. -->
        <CRow
          class="mt-3 flex-wrap align-items-start"
          v-if="state.activeTab && state.activeTab.value == 'reports'"
          v-show="state.isReportsFilterVisible"
        >
          <CCol class="w-full col-12">
            <CRow class="flex-wrap">
              <div
                class="d-flex flex-wrap align-items-end mb-2 mb-md-0 min-w-0 mw-100"
              >
                <CustomSelect
                  v-if="hasSelectableGradeYears"
                  v-model="state.filterParams.by_school_year"
                  class="select-width-200 me-2 mb-2 mb-sm-0"
                  :options="gradeYears"
                  placeholder="Select year"
                  :close-on-select="true"
                  :invalid-feedback="null"
                  input-id="tardy-rpt-school-year-reports"
                  :accessible-label="$t('common.school_year')"
                  @update:model-value="setCalendarGradeYears"
                ></CustomSelect>
                <CustomSchoolYearSelectDatePicker
                  class="tardy-report-datatable-container-date-picker mb-2 mb-sm-0"
                  v-model="state.selectedDateRange"
                  :range-options="optionalDateRanges"
                  :is-current-school-year-selected="currentSchoolYearSelected"
                  :selected-school-year-limit="
                    state.filterParams.by_school_year
                  "
                  range-input-id="tardy-rpt-date-range-reports"
                  :range-accessible-label="$t('common.date_range')"
                ></CustomSchoolYearSelectDatePicker>
              </div>
              <div class="w-100 tardy-rpt-filter-check-group">
                <p
                  id="tardy-rpt-filter-options-reports"
                  class="v3-form-label mb-1"
                >
                  {{ $t("common.tardy_report_filter_options") }}
                </p>
                <div
                  role="group"
                  aria-labelledby="tardy-rpt-filter-options-reports"
                  class="d-flex flex-wrap align-items-center gap-2 w-100"
                >
                  <CustomCheck
                    class="tardy-report-datatable-container-checkbox"
                    :model-value="state.lts"
                    :label="'LTS'"
                    @change="changeLTS"
                  ></CustomCheck>
                  <CustomCheck
                    class="tardy-report-datatable-container-checkbox"
                    :model-value="state.ltc"
                    :label="'LTC'"
                    @change="changeLTC"
                  ></CustomCheck>
                  <CustomCheck
                    class="tardy-report-datatable-container-checkbox mw-fit"
                    v-model="state.studentNumber.reports"
                    label="Student Number"
                  ></CustomCheck>
                </div>
              </div>
            </CRow>
          </CCol>
        </CRow>
        <CRow
          class="mt-3 flex-wrap align-items-start"
          v-if="state.activeTab && state.activeTab.value == 'reports'"
        >
          <div
            class="col-12 text-md-end tardy-rpt-filter-actions"
            data-testid="tardy-rpt-reports-actions"
          >
            <BaseButton
              solid
              rounded
              data-testid="tardy-rpt-reports-submit"
              @click="getReports(null, 1)"
              >Submit</BaseButton
            >
            <BaseButton
              class="ms-3"
              rounded
              data-testid="tardy-rpt-reports-clear"
              @click="clearAll()"
              >Clear all</BaseButton
            >
          </div>
        </CRow>
        <CRow
          class="mt-3 flex-wrap align-items-start"
          v-if="state.activeTab && state.activeTab.value == 'detailed'"
          v-show="state.isDetailedReportsFilterVisible"
        >
          <CCol class="w-full col-12">
            <CRow class="flex-wrap">
              <LazyLoadSelect
                class="tardy-report-datatable-container-select mb-2"
                placeholder="Select student"
                type="student"
                :selected="state.selectedStudent"
                :show-inactive="true"
                :accessible-label="$t('common.student')"
                @changed="
                  (value) => {
                    state.selectedStudent = value
                  }
                "
              >
              </LazyLoadSelect>
              <div
                class="d-flex flex-wrap align-items-end mb-2 mb-md-0 min-w-0 mw-100"
              >
                <CustomSelect
                  v-if="hasSelectableGradeYears"
                  v-model="state.filterParams.by_school_year"
                  class="select-width-200 me-2 mb-2 mb-sm-0"
                  :options="gradeYears"
                  placeholder="Select year"
                  :close-on-select="true"
                  :invalid-feedback="null"
                  input-id="tardy-rpt-school-year-detailed"
                  :accessible-label="$t('common.school_year')"
                  @update:model-value="setCalendarGradeYears"
                ></CustomSelect>
                <CustomSchoolYearSelectDatePicker
                  class="tardy-report-datatable-container-date-picker mb-2 mb-sm-0"
                  v-model="state.selectedDateRange"
                  :range-options="optionalDateRanges"
                  :is-current-school-year-selected="currentSchoolYearSelected"
                  :selected-school-year-limit="
                    state.filterParams.by_school_year
                  "
                  range-input-id="tardy-rpt-date-range-detailed"
                  :range-accessible-label="$t('common.date_range')"
                ></CustomSchoolYearSelectDatePicker>
              </div>
              <div class="w-100 tardy-rpt-filter-check-group">
                <p
                  id="tardy-rpt-filter-options-detailed"
                  class="v3-form-label mb-1"
                >
                  {{ $t("common.tardy_report_filter_options") }}
                </p>
                <div
                  role="group"
                  aria-labelledby="tardy-rpt-filter-options-detailed"
                  class="d-flex flex-wrap align-items-center gap-2 w-100"
                >
                  <CustomCheck
                    class="tardy-report-datatable-container-checkbox"
                    :model-value="state.lts"
                    :label="'LTS'"
                    @change="changeLTS"
                  ></CustomCheck>
                  <CustomCheck
                    class="tardy-report-datatable-container-checkbox"
                    :model-value="state.ltc"
                    :label="'LTC'"
                    @change="changeLTC"
                  ></CustomCheck>
                  <CustomCheck
                    class="tardy-report-datatable-container-checkbox mw-fit"
                    v-model="state.studentNumber.detailedReports"
                    label="Student Number"
                  ></CustomCheck>
                </div>
              </div>
            </CRow>
          </CCol>
        </CRow>
        <CRow
          class="mt-3 flex-wrap align-items-start"
          v-if="state.activeTab && state.activeTab.value == 'detailed'"
        >
          <div
            class="col-12 text-md-end tardy-rpt-filter-actions"
            data-testid="tardy-rpt-detailed-actions"
          >
            <BaseButton
              solid
              rounded
              data-testid="tardy-rpt-detailed-submit"
              @click="getDetailedReports(null, 1)"
              >Submit</BaseButton
            >
            <BaseButton
              class="ms-4"
              rounded
              data-testid="tardy-rpt-detailed-clear"
              @click="clearAll()"
              >Clear all</BaseButton
            >
          </div>
        </CRow>
      </template>
    </ActionPanel>

    <PerPage
      class="mb-3"
      v-if="state.pagination"
      :pagination="state.pagination"
      :showing-entries="true"
      @on-page-change="setPerPage"
    />

    <DataTable
      v-if="activeTable.items"
      class="text-center"
      :fields="activeTable.fields"
      :items="activeTable.items"
    >
      <template #student_first_name="{ item }">
        <td>{{ item.user_first_name }}</td>
      </template>
      <template #student_last_name="{ item }">
        <td>{{ item.user_last_name }}</td>
      </template>
      <template #student_number="{ item }">
        <td>{{ item.user_student_sis_id }}</td>
      </template>
      <template #teacher_name="{ item }">
        <td>
          {{
            item.teacher_name && item.tardy_badge === "LTC"
              ? item.teacher_name
              : "-"
          }}
        </td>
      </template>
      <template #date="{ item }">
        <td>{{ item.date }}</td>
      </template>
      <template #tardy_badge="{ item }">
        <td>
          <img
            v-if="item.tardy_badge === 'LTC'"
            width="25"
            height="30"
            src="@/assets/images/late-to-class.png"
            :alt="$t('common.late_to_class')"
          />
          <img
            v-if="item.tardy_badge === 'LTS'"
            width="25"
            height="30"
            src="@/assets/images/late-to-school.png"
            :alt="$t('common.late_to_school')"
          />
        </td>
      </template>
      <template #date_range_total="{ item }">
        <td>{{ item.date_range_total }}</td>
      </template>
      <template #school_year_total="{ item }">
        <td>{{ item.school_year_total }}</td>
      </template>
    </DataTable>
    <Pagination
      v-if="
        state.pagination.pages &&
        state.pagination.pages > 1 &&
        !state.isLazyLoadingMode
      "
      :active-page="state.pagination.activePage"
      :pages="state.pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    ></Pagination>
  </div>
</template>
<script>
import Loader from "@/v3components/shared/Loader/Loader.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSchoolYearSelectDatePicker from "@/v3components/shared/Form/CustomSchoolYearSelectDatePicker.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import moment from "moment-timezone"
import helpers from "../../helpers/index.js"
import { useStore } from "vuex"
import { onMounted, onUnmounted, computed, reactive, watch } from "vue"
import { isAllEntriesPerPageOption } from "@/constants/tablePaginationConstants.js"
import { attachMainLayoutLazyScroll } from "@/helpers/tableLazyScroll.js"
import { useGradeYears } from "@/composables/useGradeYears.js"
import { useToast } from "vue-toastification"

export default {
  name: "TardyReportsTable",
  components: {
    Loader,
    ActionPanel,
    DataTable,
    Pagination,
    CustomCheck,
    CustomSelect,
    CustomSchoolYearSelectDatePicker,
    LazyLoadSelect,
    BaseButton,
    PerPage
  },
  setup() {
    const store = useStore()
    const toast = useToast()
    const reportReadyEventPayload = computed(() => {
      return store?.getters?.["reports/reportReadyEventPayload"] || null
    })
    const detailedReportReadyEventPayload = computed(() => {
      return store?.getters?.["reports/detailedReportReadyEventPayload"] || null
    })
    const {
      gradeYears,
      currentSchoolYear,
      hasSelectableGradeYears,
      getMinDate,
      getMaxDate,
      watchSelectedGradeYear
    } = useGradeYears()

    const activeSchoolTimeZone = computed(() => {
      return store.getters["schools/activeSchoolTimeZone"]
    })

    const DEFAULT_ACTIVE_TAB = {
      label: "LTS & LTC",
      value: "reports"
    }

    const tabs = computed(() => [
      {
        label: DEFAULT_ACTIVE_TAB.label,
        value: DEFAULT_ACTIVE_TAB.value
      },
      {
        label: "Detailed",
        value: "detailed"
      }
    ])

    const state = reactive({
      isReportsFilterVisible: true,
      isDetailedReportsFilterVisible: true,
      activeTab: DEFAULT_ACTIVE_TAB,
      lts: true,
      ltc: true,
      studentNumber: {
        reports: true,
        detailedReports: true
      },
      selectedStudent: null,
      selectedDateRange: null,
      isLazyLoadingMode: false,
      isLoading: false,
      isMounted: false,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      filterParams: {
        by_school_year: [],
        dates:
          moment().format("MM/DD/YYYY") + " - " + moment().format("MM/DD/YYYY")
      },
      getReportResponse: null
    })

    onMounted(() => {
      state.isMounted = true
      state.filterParams.by_school_year = gradeYears.value
        ? gradeYears.value[gradeYears.value.length - 1]
        : state.filterParams.by_school_year
      initLazyLoading()
    })

    onUnmounted(() => {
      detachMainLayoutLazyScroll?.()
      state.isMounted = false
    })

    // tableFields
    const reportTableFields = [
      {
        key: "student_first_name",
        label: "Student FN",
        sorter: false,
        filter: false
      },
      {
        key: "student_last_name",
        label: "Student LN",
        sorter: false,
        filter: false
      },
      {
        key: "student_number",
        label: "Student number",
        sorter: false,
        filter: false
      },
      { key: "date", label: "Date", sorter: false, filter: false },
      { key: "tardy_badge", label: "Tardy type", sorter: false, filter: false },

      {
        key: "date_range_total",
        label: "Date range total",
        sorter: false,
        filter: false
      },
      {
        key: "school_year_total",
        label: "School year total",
        sorter: false,
        filter: false
      }
    ]

    const detailedTableFields = [
      {
        key: "student_first_name",
        label: "Student FN",
        sorter: false,
        filter: false
      },
      {
        key: "student_last_name",
        label: "Student LN",
        sorter: false,
        filter: false
      },
      {
        key: "student_number",
        label: "Student number",
        sorter: false,
        filter: false
      },
      {
        key: "teacher_name",
        label: "Teacher name",
        sorter: false,
        filter: false
      },
      { key: "date", label: "Date & Time", sorter: false, filter: false },
      { key: "tardy_badge", label: "Tardy type", sorter: false, filter: false },

      {
        key: "date_range_total",
        label: "Date range total",
        sorter: false,
        filter: false
      },
      {
        key: "school_year_total",
        label: "School year total",
        sorter: false,
        filter: false
      }
    ]
    // reports
    const reports = computed(() => store.getters["reports/reports"])

    const minDate = computed(() => {
      return getMinDate(state.filterParams.by_school_year)
    })

    const maxDate = computed(() => {
      return getMaxDate(state.filterParams.by_school_year)
    })

    const currentSchoolYearSelected = computed(() => {
      return (
        state.filterParams.by_school_year?.label ==
        currentSchoolYear.value?.label
      )
    })

    const optionalDateRanges = computed(() => {
      const selectedSchoolYear = state.filterParams?.by_school_year?.value

      if (!selectedSchoolYear || !minDate.value) {
        return [
          {
            label: "Today",
            id: "",
            range: helpers.stringDateRange(moment(), moment())
          }
        ]
      }

      const getRange = (days) => {
        const today = moment()
        const start = today.clone().subtract(days, "days")

        const minSchoolYearDate = moment({
          year: minDate.value.getFullYear(),
          month: minDate.value.getMonth(),
          date: minDate.value.getDate()
        })

        const adjustedStart = start.isBefore(minSchoolYearDate)
          ? minSchoolYearDate
          : start

        return helpers.stringDateRange(adjustedStart, today)
      }

      return [
        {
          label: "Today",
          id: "",
          range: helpers.stringDateRange(moment(), moment())
        },
        {
          label: "Yesterday",
          id: "",
          range: helpers.stringDateRange(
            moment().subtract(1, "days"),
            moment().subtract(1, "days")
          )
        },
        {
          label: "Last 7 days",
          id: "",
          range: getRange(7)
        },
        {
          label: "Last 14 days",
          id: "",
          range: getRange(14)
        },
        {
          label: "Last 30 days",
          id: "",
          range: getRange(30)
        },
        {
          label: "This month",
          id: "",
          range: helpers.stringDateRange(
            moment().startOf("month"),
            moment().endOf("month")
          )
        },
        {
          label: "This year",
          id: "",
          range:
            helpers.currTzDate(minDate.value) +
            " - " +
            helpers.currTzDate(maxDate.value)
        },
        {
          label: "Custom Range",
          id: "custom",
          range: null
        }
      ]
    })

    const setCalendarGradeYears = () => {
      state.selectedDateRange = `${helpers.currTzDate(minDate.value)} - ${helpers.currTzDate(maxDate.value)}`
    }

    watchSelectedGradeYear(
      () => state.filterParams.by_school_year,
      (nextGradeYear) => {
        state.filterParams.by_school_year = nextGradeYear
      },
      () => {
        setCalendarGradeYears()
      }
    )

    const getReportsParams = () => {
      return {
        page: state.pagination.activePage,
        per_page: [25, 50, 100].includes(state.pagination.per_page.value)
          ? state.pagination.per_page.value
          : "all",
        between_dates: state.selectedDateRange
          ? state.selectedDateRange
              .replace(/\//g, ".")
              .split(" - ")
              .map((date) => moment(date, "MM/DD/YYYY").format("MM/DD/YYYY"))
          : [moment().format("MM/DD/YYYY"), moment().format("MM/DD/YYYY")],
        tardy_badges:
          state.lts && state.ltc
            ? ["LTC", "LTS"]
            : state.lts
              ? ["LTS"]
              : state.ltc
                ? ["LTC"]
                : !state.lts && !state.ltc
                  ? [""]
                  : null,
        student_sis_id: state.studentNumber.reports
      }
    }

    const getReports = (isLazyLoadingMode, page) => {
      state.pagination.activePage = page ? page : 1
      const params = Object.assign(getReportsParams())
      state.isLoading = true

      store.dispatch("reports/getReports", params).then((response) => {
        state.getReportResponse = response?.data
        // Data will be handled when the socket event triggers fetchReportData
      })
    }

    // detailed
    const detailedReports = computed(
      () => store.getters["reports/detailedReports"]
    )

    const getDetailedReportsParams = () => {
      return {
        per_page: [25, 50, 100].includes(state.pagination.per_page.value)
          ? state.pagination.per_page.value
          : "all",
        page: state.pagination.activePage,
        between_dates: state.selectedDateRange
          ? state.selectedDateRange
              .replace(/\//g, ".")
              .split(" - ")
              .map((date) => moment(date, "MM/DD/YYYY").format("MM/DD/YYYY"))
          : [moment().format("MM/DD/YYYY"), moment().format("MM/DD/YYYY")],
        tardy_badges:
          state.lts && state.ltc
            ? ["LTC", "LTS"]
            : state.lts
              ? ["LTS"]
              : state.ltc
                ? ["LTC"]
                : !state.lts && !state.ltc
                  ? [""]
                  : null,
        student_id: state.selectedStudent
          ? state.selectedStudent.value.id
          : null,
        student_sis_id: state.studentNumber.detailedReports
      }
    }

    const getDetailedReports = (isLazyLoadingMode, page) => {
      state.pagination.activePage = page ? page : 1
      const params = Object.assign(getDetailedReportsParams())
      state.isLoading = true
      store.dispatch("reports/getDetailedReports", params).then((response) => {
        state.getReportResponse = response?.data
      })
    }

    watch(
      () => detailedReportReadyEventPayload.value,
      (readyDetailedReportValue) => {
        if (readyDetailedReportValue) {
          const params = Object.assign(getReportsParams(), {
            reportKey: readyDetailedReportValue.reportKey,
            perPage: [25, 50, 100].includes(state.pagination.per_page.value)
              ? state.pagination.per_page.value
              : "all",
            page: state.pagination.activePage
          })
          store
            .dispatch("reports/fetchReportData", params)
            .then((response) => {
              const data = response.data
              if (state.isLazyLoadingMode) {
                store.commit("reports/PUSH_DETAILED_REPORTS", data.data)
              } else {
                store.commit("reports/SET_DETAILED_REPORTS", data.data)
              }
              if (data.meta) {
                setPagination({
                  total: data.meta.total,
                  from: data.meta.from,
                  to: data.meta.to,
                  pages: Math.ceil(data.meta.total / data.meta.per_page),
                  activePage: Number(state.pagination.activePage)
                })
              }
              const deleteParams = Object.assign(getReportsParams(), {
                reportKey: readyDetailedReportValue.reportKey
              })
              store
                .dispatch("reports/deleteReportData", deleteParams)
                .catch(() => {
                  toast.error(
                    "Error deleting report data. Please try again later."
                  )
                })
              state.isLoading = false
            })
            .catch((error) => {
              state.isLoading = false
              if (
                error?.response?.data?.error.status_code == 504 &&
                state.isMounted
              ) {
                toast.error(
                  "Search criteria is too large. Please reduce the date range and try again."
                )
              }
            })
        }
      }
    )

    const reportsCollection = computed(() =>
      reports?.value?.map((report) => {
        const date = moment
          .utc(report.date)
          .tz(activeSchoolTimeZone.value)
          .format("MM/DD/YYYY")
        return {
          ...report,
          date
        }
      })
    )

    const detailedReportsCollection = computed(() =>
      detailedReports.value.map((detailedReport) => {
        const date = moment
          .utc(detailedReport.date)
          .tz(activeSchoolTimeZone.value)
          .format("MM/DD/YYYY h:mm a")
        return {
          ...detailedReport,
          date
        }
      })
    )

    const isReportTab = computed(
      () => state.activeTab && state.activeTab.value === "reports"
    )
    const filterTableFields = (fields = [], condition = false) => {
      if (!fields.length) return
      return fields.filter((field) =>
        !condition ? field.key !== "student_number" : field
      )
    }
    const activeTable = computed(() => {
      return !isReportTab.value
        ? {
            items: detailedReportsCollection.value,
            fields: filterTableFields(
              detailedTableFields,
              state.studentNumber.detailedReports
            )
          }
        : {
            items: reportsCollection.value,
            fields: filterTableFields(
              reportTableFields,
              state.studentNumber.reports
            )
          }
    })

    const resetFilterValues = () => {
      state.isLazyLoadingMode = false
      state.isReportsFilterVisible = true
      state.isDetailedReportsFilterVisible = true
      state.lts = true
      state.ltc = true
      state.selectedStudent = null
      state.filterParams.by_school_year = gradeYears.value
        ? gradeYears.value[gradeYears.value.length - 1]
        : state.filterParams.by_school_year

      // Add safety check for optionalDateRanges
      if (optionalDateRanges.value && optionalDateRanges.value.length > 0) {
        state.selectedDateRange = optionalDateRanges.value[0].range
      } else {
        // Fallback to current date range if optionalDateRanges is not available
        state.selectedDateRange =
          moment().format("MM/DD/YYYY") + " - " + moment().format("MM/DD/YYYY")
      }

      // Clear existing data when resetting filters
      store.commit("reports/SET_REPORTS", [])
      store.commit("reports/SET_DETAILED_REPORTS", [])

      setPagination({
        activePage: 1,
        total: 0,
        from: 0,
        to: 0,
        pages: 0
      })
    }
    const setActiveTab = (tab) => {
      state.activeTab = tab
      resetFilterValues()
      helpers.scrollToTop()

      // Clear both reports data when switching tabs
      store.commit("reports/SET_REPORTS", [])
      store.commit("reports/SET_DETAILED_REPORTS", [])

      if (tab.value === "reports") {
        try {
          getReports()
        } catch (error) {
          throw new Error(error)
        }
      }
    }
    const setPagination = (data) => {
      state.pagination = Object.assign(state.pagination, data)
    }

    const setActivePage = (page) => {
      isReportTab.value
        ? getReports(null, page)
        : getDetailedReports(null, page)
      helpers.scrollToTop()
    }

    const toggleFilter = () => {
      isReportTab.value
        ? (state.isReportsFilterVisible = !state.isReportsFilterVisible)
        : (state.isDetailedReportsFilterVisible =
            !state.isDetailedReportsFilterVisible)
    }

    const exportCSV = () => {
      isReportTab.value ? exportReports() : exportDetailedReports()
    }

    const exportReports = () => {
      const params = Object.assign(getReportsParams(), {
        page: state.pagination.activePage,
        per_page: [25, 50, 100].includes(state.pagination.per_page.value)
          ? state.pagination.per_page.value
          : "all",
        student_sis_id: state.studentNumber.reports
      })

      state.isLoading = true

      store
        .dispatch("reports/getReportsCSV", params)
        .then((response) => {
          if (response) {
            state.isLoading = false
            // Only show toast when "All entries" is selected
            if (state.isLazyLoadingMode) {
              toast.success(
                "The report is too large to download instantly. When completed, your report will be available on your device."
              )
            }
          }
        })
        .catch((error) => {
          state.isLoading = false
          if (
            error?.response?.data?.error.status_code == 504 &&
            state.isMounted
          ) {
            toast.error(
              "Search criteria is too large. Please reduce the date range and try again."
            )
          }
        })
    }

    const exportDetailedReports = () => {
      const params = Object.assign(getDetailedReportsParams(), {
        page: state.pagination.activePage,
        per_page: [25, 50, 100].includes(state.pagination.per_page.value)
          ? state.pagination.per_page.value
          : "all",
        student_sis_id: state.studentNumber.reports
      })

      state.isLoading = true

      store
        .dispatch("reports/getDetailedReportsCSV", params)
        .then((response) => {
          if (response) {
            state.isLoading = false
            // Only show toast when "All entries" is selected
            if (state.isLazyLoadingMode) {
              toast.success(
                "The report is too large to download instantly. When completed, your report will be available on your device."
              )
            }
          }
        })
        .catch((error) => {
          state.isLoading = false
          if (
            error?.response?.data?.error.status_code == 504 &&
            state.isMounted
          ) {
            toast.error(
              "Search criteria is too large. Please reduce the date range and try again."
            )
          }
        })
    }

    const changeLTS = () => {
      state.lts = !state.lts
    }

    const changeLTC = () => {
      state.ltc = !state.ltc
    }

    const clearAll = () => {
      resetFilterValues()
      isReportTab.value ? getReports() : getDetailedReports()
    }

    const setPerPage = (option) => {
      if (option) {
        state.isLazyLoadingMode = isAllEntriesPerPageOption(option)
        state.pagination.per_page = option

        // Clear existing data when changing per page to prevent duplication
        if (isReportTab.value) {
          store.commit("reports/SET_REPORTS", [])
        } else {
          store.commit("reports/SET_DETAILED_REPORTS", [])
        }

        // Reset pagination to first page
        state.pagination.activePage = 1

        // Fetch new data
        if (isReportTab.value) {
          getReports()
        } else {
          getDetailedReports()
        }
      }
    }

    let detachMainLayoutLazyScroll = null
    const initLazyLoading = () => {
      detachMainLayoutLazyScroll?.()
      detachMainLayoutLazyScroll = attachMainLayoutLazyScroll(() => {
        if (isReportTab.value) {
          if (
            !state.isLazyLoadingMode ||
            !reports.value ||
            !reports.value.length ||
            state.pagination.total <= reports.value.length
          ) {
            return
          }
          state.pagination.activePage = state.pagination.activePage + 1
          getReports(true, state.pagination.activePage)
        } else {
          if (
            !state.isLazyLoadingMode ||
            !detailedReports.value ||
            !detailedReports.value.length ||
            state.pagination.total <= detailedReports.value.length
          ) {
            return
          }
          state.pagination.activePage = state.pagination.activePage + 1
          getDetailedReports(true, state.pagination.activePage)
        }
      })
    }

    watch(
      () => reportReadyEventPayload.value,
      (readyReportValue) => {
        if (readyReportValue) {
          const params = Object.assign(getReportsParams(), {
            reportKey: readyReportValue.reportKey,
            perPage: [25, 50, 100].includes(state.pagination.per_page.value)
              ? state.pagination.per_page.value
              : "all",
            page: state.pagination.activePage
          })
          store
            .dispatch("reports/fetchReportData", params)
            .then((response) => {
              const data = response.data
              if (state.isLazyLoadingMode) {
                store.commit("reports/PUSH_REPORTS", data.data)
              } else {
                store.commit("reports/SET_REPORTS", data.data)
              }
              if (data.meta) {
                setPagination({
                  total: data.meta.total,
                  from: data.meta.from,
                  to: data.meta.to,
                  pages: Math.ceil(data.meta.total / data.meta.per_page),
                  activePage: Number(state.pagination.activePage)
                })
              }
              const deleteParams = Object.assign(getReportsParams(), {
                reportKey: readyReportValue.reportKey
              })
              store
                .dispatch("reports/deleteReportData", deleteParams)
                .catch(() => {
                  toast.error(
                    "Error deleting report data. Please try again later."
                  )
                })
              state.isLoading = false
            })
            .catch((error) => {
              state.isLoading = false
              if (
                error?.response?.data?.error.status_code == 504 &&
                state.isMounted
              ) {
                toast.error(
                  "Search criteria is too large. Please reduce the date range and try again."
                )
              }
            })
        }
      }
    )

    return {
      state,
      reportReadyEventPayload,
      detailedReportReadyEventPayload,
      tabs,
      setActiveTab,
      reportTableFields,
      detailedTableFields,
      reports,
      minDate,
      maxDate,
      gradeYears,
      hasSelectableGradeYears,
      activeSchoolTimeZone,
      setCalendarGradeYears,
      currentSchoolYearSelected,
      optionalDateRanges,
      setActivePage,
      activeTable,
      toggleFilter,
      exportCSV,
      changeLTS,
      changeLTC,
      getReports,
      getDetailedReports,
      clearAll,
      setPerPage
    }
  }
}
</script>

<style lang="scss" scoped>
.select-width-200 {
  width: 200px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 575.98px) {
    width: 100%;
    flex: 1 1 100%;
  }
}
.tardy-rpt-filter-check-group {
  margin-top: 10px;
}
</style>
